import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, X, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { EASE } from "./motion";

const CONTACT_EMAIL = "info@famjobconsultancy.com";
const inputCls = "w-full border border-[#0A1128]/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#1D4ED8] transition-colors";

export default function EmailPopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Message sent. Our team will email you back shortly.");
      setForm({ name: "", email: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, scale: 0, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 2.5, type: "spring", stiffness: 260, damping: 18 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.92 }}
        className="fixed bottom-24 right-6 z-[90] group flex items-center"
        data-testid="email-popup-button"
        aria-label="Email us"
      >
        <span className="max-w-0 overflow-hidden group-hover:max-w-[140px] transition-all duration-500 ease-out">
          <span className="block whitespace-nowrap bg-white text-[#0A1128] text-xs uppercase tracking-[0.15em] font-medium px-4 py-2 mr-3 shadow-lg border border-[#0A1128]/10">
            Email us
          </span>
        </span>
        <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-[#1D4ED8] text-white shadow-[0_8px_24px_rgba(29,78,216,0.45)]">
          <Mail size={22} />
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#0A1128]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setOpen(false)}
            data-testid="email-popup-overlay"
          >
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="bg-[#F4F5F7] w-full max-w-md p-8 md:p-10"
              onClick={(e) => e.stopPropagation()}
              data-testid="email-popup-dialog"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#1D4ED8] mb-2">Email Us</p>
                  <h3 className="font-display font-bold text-2xl tracking-tight text-[#0A1128]">
                    Drop us a line
                  </h3>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-[#475569] mt-1 hover:text-[#1D4ED8] transition-colors">
                    {CONTACT_EMAIL}
                  </a>
                </div>
                <button onClick={() => setOpen(false)} className="text-[#475569] hover:text-[#0A1128]" aria-label="Close" data-testid="email-popup-close">
                  <X size={22} />
                </button>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="email-popup-name" />
                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="email-popup-email" />
                <textarea required rows={4} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="email-popup-message" />
                <button disabled={busy} className="w-full flex items-center justify-center gap-2 bg-[#1D4ED8] hover:bg-[#1E3A8A] disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-4 transition-colors duration-300" data-testid="email-popup-submit">
                  <Send size={14} />
                  {busy ? "Sending…" : "Send Message"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
